/// <amd-module name="Dashboards/Common/RestClient/Dashboard" />
/**
 * ---------------------------------------------------------
 * Generated file, DO NOT EDIT
 * ---------------------------------------------------------
 *
 * See following wiki page for instructions on how to regenerate:
 *   https://aka.ms/azure-devops-client-generation
 *
 * Configuration file:
 *   tfs\client\dashboards\clientgeneratorconfigs\genclient.json
 */

import { IVssPageContext, IVssRestClient, IVssRestClientOptions, RestClients } from "VSS/Platform/Context";
import { RestClientBase } from "VSS/Platform/RestClientBase";
import { deserializeVssJsonObject } from "VSS/Platform/Util/Serialization";

import * as Dashboard from "Dashboards/Common/Contracts/Dashboard";
import * as TeamContext from "Dashboards/Common/Contracts/TeamContext";

export interface IDashboardRestClient extends IVssRestClient {
    /**
     * Copies the given dashboard to given location
     * 
     * @param copyDashboardOptions - 
     * @param teamContext - The team context for the operation
     * @param dashboardId - ID of the dashboard to copy
     */
    copyDashboard(copyDashboardOptions: Dashboard.CopyDashboardOptions, teamContext: TeamContext.TeamContext, dashboardId: string): Promise<Dashboard.CopyDashboardResponse>;
    /**
     * Create the supplied dashboard.
     * 
     * @param dashboard - The initial state of the dashboard
     * @param teamContext - The team context for the operation
     */
    createDashboard(dashboard: Dashboard.Dashboard, teamContext: TeamContext.TeamContext): Promise<Dashboard.Dashboard>;
    /**
     * Delete a dashboard given its ID. This also deletes the widgets associated with this dashboard.
     * 
     * @param teamContext - The team context for the operation
     * @param dashboardId - ID of the dashboard to delete.
     */
    deleteDashboard(teamContext: TeamContext.TeamContext, dashboardId: string): Promise<void>;
    /**
     * Get a dashboard by its ID.
     * 
     * @param teamContext - The team context for the operation
     * @param dashboardId - 
     */
    getDashboard(teamContext: TeamContext.TeamContext, dashboardId: string): Promise<Dashboard.Dashboard>;
    /**
     * Get a list of dashboards under a project.
     * 
     * @param teamContext - The team context for the operation
     */
    getDashboardsByProject(teamContext: TeamContext.TeamContext): Promise<Dashboard.Dashboard[]>;
    /**
     * Replace configuration for the specified dashboard. Replaces Widget list on Dashboard, only if property is supplied.
     * 
     * @param dashboard - The Configuration of the dashboard to replace.
     * @param teamContext - The team context for the operation
     * @param dashboardId - ID of the dashboard to replace.
     */
    replaceDashboard(dashboard: Dashboard.Dashboard, teamContext: TeamContext.TeamContext, dashboardId: string): Promise<Dashboard.Dashboard>;
    /**
     * Update the name and position of dashboards in the supplied group, and remove omitted dashboards. Does not modify dashboard content.
     * 
     * @param group - 
     * @param teamContext - The team context for the operation
     */
    replaceDashboards(group: Dashboard.DashboardGroup, teamContext: TeamContext.TeamContext): Promise<Dashboard.DashboardGroup>;
    /**
     * Create a widget on the specified dashboard.
     * 
     * @param widget - State of the widget to add
     * @param teamContext - The team context for the operation
     * @param dashboardId - ID of dashboard the widget will be added to.
     */
    createWidget(widget: Dashboard.Widget, teamContext: TeamContext.TeamContext, dashboardId: string): Promise<Dashboard.Widget>;
    /**
     * Delete the specified widget.
     * 
     * @param teamContext - The team context for the operation
     * @param dashboardId - ID of the dashboard containing the widget.
     * @param widgetId - ID of the widget to update.
     */
    deleteWidget(teamContext: TeamContext.TeamContext, dashboardId: string, widgetId: string): Promise<Dashboard.Dashboard>;
    /**
     * Get the current state of the specified widget.
     * 
     * @param teamContext - The team context for the operation
     * @param dashboardId - ID of the dashboard containing the widget.
     * @param widgetId - ID of the widget to read.
     */
    getWidget(teamContext: TeamContext.TeamContext, dashboardId: string, widgetId: string): Promise<Dashboard.Widget>;
    /**
     * Get widgets contained on the specified dashboard.
     * 
     * @param teamContext - The team context for the operation
     * @param dashboardId - ID of the dashboard to read.
     * @param eTag - Dashboard Widgets Version
     */
    getWidgets(teamContext: TeamContext.TeamContext, dashboardId: string, eTag?: String): Promise<Dashboard.WidgetsVersionedList>;
    /**
     * Override the  state of the specified widget.
     * 
     * @param widget - State to be written for the widget.
     * @param teamContext - The team context for the operation
     * @param dashboardId - ID of the dashboard containing the widget.
     * @param widgetId - ID of the widget to update.
     */
    replaceWidget(widget: Dashboard.Widget, teamContext: TeamContext.TeamContext, dashboardId: string, widgetId: string): Promise<Dashboard.Widget>;
    /**
     * Replace the widgets on specified dashboard with the supplied widgets.
     * 
     * @param widgets - Revised state of widgets to store for the dashboard.
     * @param teamContext - The team context for the operation
     * @param dashboardId - ID of the Dashboard to modify.
     * @param eTag - Dashboard Widgets Version
     */
    replaceWidgets(widgets: Dashboard.Widget[], teamContext: TeamContext.TeamContext, dashboardId: string, eTag?: String): Promise<Dashboard.WidgetsVersionedList>;
    /**
     * Perform a partial update of the specified widget.
     * 
     * @param widget - Description of the widget changes to apply. All non-null fields will be replaced.
     * @param teamContext - The team context for the operation
     * @param dashboardId - ID of the dashboard containing the widget.
     * @param widgetId - ID of the widget to update.
     */
    updateWidget(widget: Dashboard.Widget, teamContext: TeamContext.TeamContext, dashboardId: string, widgetId: string): Promise<Dashboard.Widget>;
    /**
     * Update the supplied widgets on the dashboard using supplied state. State of existing Widgets not passed in the widget list is preserved.
     * 
     * @param widgets - The set of widget states to update on the dashboard.
     * @param teamContext - The team context for the operation
     * @param dashboardId - ID of the Dashboard to modify.
     * @param eTag - Dashboard Widgets Version
     */
    updateWidgets(widgets: Dashboard.Widget[], teamContext: TeamContext.TeamContext, dashboardId: string, eTag?: String): Promise<Dashboard.WidgetsVersionedList>;
    /**
     * Get the widget metadata satisfying the specified contribution ID.
     * 
     * @param contributionId - The ID of Contribution for the Widget
     * @param project - Project ID or project name
     */
    getWidgetMetadata(contributionId: string, project?: string): Promise<Dashboard.WidgetMetadataResponse>;
    /**
     * Get all available widget metadata in alphabetical order, including widgets marked with isVisibleFromCatalog == false.
     * 
     * @param scope - 
     * @param project - Project ID or project name
     */
    getWidgetTypes(scope: Dashboard.WidgetScope, project?: string): Promise<Dashboard.WidgetTypesResponse>;
}

class DashboardRestClient extends RestClientBase implements IDashboardRestClient {
    constructor(options: IVssRestClientOptions) {
        super(options);
    }

    /**
     * Copies the given dashboard to given location
     * 
     * @param copyDashboardOptions - 
     * @param teamContext - The team context for the operation
     * @param dashboardId - ID of the dashboard to copy
     */
    public async copyDashboard(
        copyDashboardOptions: Dashboard.CopyDashboardOptions,
        teamContext: TeamContext.TeamContext,
        dashboardId: string
        ): Promise<Dashboard.CopyDashboardResponse> {

        const project = teamContext.projectId || teamContext.project;
        const team = teamContext.teamId || teamContext.team;

        return this.beginRequest<Dashboard.CopyDashboardResponse>({
            apiVersion: "6.0-preview.3",
            method: "POST",
            routeTemplate: "{project}/{team}/_apis/Dashboard/Dashboards/{dashboardId}",
            routeValues: {
                project: project,
                team: team,
                dashboardId: dashboardId
            },
            body: copyDashboardOptions
        });
    }

    /**
     * Create the supplied dashboard.
     * 
     * @param dashboard - The initial state of the dashboard
     * @param teamContext - The team context for the operation
     */
    public async createDashboard(
        dashboard: Dashboard.Dashboard,
        teamContext: TeamContext.TeamContext
        ): Promise<Dashboard.Dashboard> {

        const project = teamContext.projectId || teamContext.project;
        const team = teamContext.teamId || teamContext.team;

        return this.beginRequest<Dashboard.Dashboard>({
            apiVersion: "6.0-preview.3",
            method: "POST",
            routeTemplate: "{project}/{team}/_apis/Dashboard/Dashboards/{dashboardId}",
            routeValues: {
                project: project,
                team: team
            },
            body: dashboard
        });
    }

    /**
     * Delete a dashboard given its ID. This also deletes the widgets associated with this dashboard.
     * 
     * @param teamContext - The team context for the operation
     * @param dashboardId - ID of the dashboard to delete.
     */
    public async deleteDashboard(
        teamContext: TeamContext.TeamContext,
        dashboardId: string
        ): Promise<void> {

        const project = teamContext.projectId || teamContext.project;
        const team = teamContext.teamId || teamContext.team;

        return this.beginRequest<void>({
            apiVersion: "6.0-preview.3",
            method: "DELETE",
            routeTemplate: "{project}/{team}/_apis/Dashboard/Dashboards/{dashboardId}",
            routeValues: {
                project: project,
                team: team,
                dashboardId: dashboardId
            }
        });
    }

    /**
     * Get a dashboard by its ID.
     * 
     * @param teamContext - The team context for the operation
     * @param dashboardId - 
     */
    public async getDashboard(
        teamContext: TeamContext.TeamContext,
        dashboardId: string
        ): Promise<Dashboard.Dashboard> {

        const project = teamContext.projectId || teamContext.project;
        const team = teamContext.teamId || teamContext.team;

        return this.beginRequest<Dashboard.Dashboard>({
            apiVersion: "6.0-preview.3",
            routeTemplate: "{project}/{team}/_apis/Dashboard/Dashboards/{dashboardId}",
            routeValues: {
                project: project,
                team: team,
                dashboardId: dashboardId
            }
        });
    }

    /**
     * Get a list of dashboards under a project.
     * 
     * @param teamContext - The team context for the operation
     */
    public async getDashboardsByProject(
        teamContext: TeamContext.TeamContext
        ): Promise<Dashboard.Dashboard[]> {

        const project = teamContext.projectId || teamContext.project;
        const team = teamContext.teamId || teamContext.team;

        return this.beginRequest<Dashboard.Dashboard[]>({
            apiVersion: "6.0-preview.3",
            routeTemplate: "{project}/{team}/_apis/Dashboard/Dashboards/{dashboardId}",
            routeValues: {
                project: project,
                team: team
            }
        });
    }

    /**
     * Replace configuration for the specified dashboard. Replaces Widget list on Dashboard, only if property is supplied.
     * 
     * @param dashboard - The Configuration of the dashboard to replace.
     * @param teamContext - The team context for the operation
     * @param dashboardId - ID of the dashboard to replace.
     */
    public async replaceDashboard(
        dashboard: Dashboard.Dashboard,
        teamContext: TeamContext.TeamContext,
        dashboardId: string
        ): Promise<Dashboard.Dashboard> {

        const project = teamContext.projectId || teamContext.project;
        const team = teamContext.teamId || teamContext.team;

        return this.beginRequest<Dashboard.Dashboard>({
            apiVersion: "6.0-preview.3",
            method: "PUT",
            routeTemplate: "{project}/{team}/_apis/Dashboard/Dashboards/{dashboardId}",
            routeValues: {
                project: project,
                team: team,
                dashboardId: dashboardId
            },
            body: dashboard
        });
    }

    /**
     * Update the name and position of dashboards in the supplied group, and remove omitted dashboards. Does not modify dashboard content.
     * 
     * @param group - 
     * @param teamContext - The team context for the operation
     */
    public async replaceDashboards(
        group: Dashboard.DashboardGroup,
        teamContext: TeamContext.TeamContext
        ): Promise<Dashboard.DashboardGroup> {

        const project = teamContext.projectId || teamContext.project;
        const team = teamContext.teamId || teamContext.team;

        return this.beginRequest<Dashboard.DashboardGroup>({
            apiVersion: "6.0-preview.3",
            method: "PUT",
            routeTemplate: "{project}/{team}/_apis/Dashboard/Dashboards/{dashboardId}",
            routeValues: {
                project: project,
                team: team
            },
            body: group
        });
    }

    /**
     * Create a widget on the specified dashboard.
     * 
     * @param widget - State of the widget to add
     * @param teamContext - The team context for the operation
     * @param dashboardId - ID of dashboard the widget will be added to.
     */
    public async createWidget(
        widget: Dashboard.Widget,
        teamContext: TeamContext.TeamContext,
        dashboardId: string
        ): Promise<Dashboard.Widget> {

        const project = teamContext.projectId || teamContext.project;
        const team = teamContext.teamId || teamContext.team;

        return this.beginRequest<Dashboard.Widget>({
            apiVersion: "6.0-preview.2",
            method: "POST",
            routeTemplate: "{project}/{team}/_apis/Dashboard/dashboards/{dashboardId}/Widgets/{widgetId}",
            routeValues: {
                project: project,
                team: team,
                dashboardId: dashboardId
            },
            body: widget
        });
    }

    /**
     * Delete the specified widget.
     * 
     * @param teamContext - The team context for the operation
     * @param dashboardId - ID of the dashboard containing the widget.
     * @param widgetId - ID of the widget to update.
     */
    public async deleteWidget(
        teamContext: TeamContext.TeamContext,
        dashboardId: string,
        widgetId: string
        ): Promise<Dashboard.Dashboard> {

        const project = teamContext.projectId || teamContext.project;
        const team = teamContext.teamId || teamContext.team;

        return this.beginRequest<Dashboard.Dashboard>({
            apiVersion: "6.0-preview.2",
            method: "DELETE",
            routeTemplate: "{project}/{team}/_apis/Dashboard/dashboards/{dashboardId}/Widgets/{widgetId}",
            routeValues: {
                project: project,
                team: team,
                dashboardId: dashboardId,
                widgetId: widgetId
            }
        });
    }

    /**
     * Get the current state of the specified widget.
     * 
     * @param teamContext - The team context for the operation
     * @param dashboardId - ID of the dashboard containing the widget.
     * @param widgetId - ID of the widget to read.
     */
    public async getWidget(
        teamContext: TeamContext.TeamContext,
        dashboardId: string,
        widgetId: string
        ): Promise<Dashboard.Widget> {

        const project = teamContext.projectId || teamContext.project;
        const team = teamContext.teamId || teamContext.team;

        return this.beginRequest<Dashboard.Widget>({
            apiVersion: "6.0-preview.2",
            routeTemplate: "{project}/{team}/_apis/Dashboard/dashboards/{dashboardId}/Widgets/{widgetId}",
            routeValues: {
                project: project,
                team: team,
                dashboardId: dashboardId,
                widgetId: widgetId
            }
        });
    }

    /**
     * Get widgets contained on the specified dashboard.
     * 
     * @param teamContext - The team context for the operation
     * @param dashboardId - ID of the dashboard to read.
     * @param eTag - Dashboard Widgets Version
     */
    public async getWidgets(
        teamContext: TeamContext.TeamContext,
        dashboardId: string,
        eTag?: String
        ): Promise<Dashboard.WidgetsVersionedList> {

        const project = teamContext.projectId || teamContext.project;
        const team = teamContext.teamId || teamContext.team;

        return this.beginRequest<Response>({
            apiVersion: "6.0-preview.2",
            routeTemplate: "{project}/{team}/_apis/Dashboard/dashboards/{dashboardId}/Widgets/{widgetId}",
            routeValues: {
                project: project,
                team: team,
                dashboardId: dashboardId
            },
            customHeaders: {
                "ETag": eTag,
            },
            returnRawResponse: true
        }).then(async response => {
            const body = await response.text().then(deserializeVssJsonObject);
            const eTagHeader = response.headers.get("ETag");
            const result: Dashboard.WidgetsVersionedList = {
                widgets: <Dashboard.Widget[]>body,
                eTag: eTagHeader ? eTagHeader.split(',') : []
            };
            return result;
        });
    }

    /**
     * Override the  state of the specified widget.
     * 
     * @param widget - State to be written for the widget.
     * @param teamContext - The team context for the operation
     * @param dashboardId - ID of the dashboard containing the widget.
     * @param widgetId - ID of the widget to update.
     */
    public async replaceWidget(
        widget: Dashboard.Widget,
        teamContext: TeamContext.TeamContext,
        dashboardId: string,
        widgetId: string
        ): Promise<Dashboard.Widget> {

        const project = teamContext.projectId || teamContext.project;
        const team = teamContext.teamId || teamContext.team;

        return this.beginRequest<Dashboard.Widget>({
            apiVersion: "6.0-preview.2",
            method: "PUT",
            routeTemplate: "{project}/{team}/_apis/Dashboard/dashboards/{dashboardId}/Widgets/{widgetId}",
            routeValues: {
                project: project,
                team: team,
                dashboardId: dashboardId,
                widgetId: widgetId
            },
            body: widget
        });
    }

    /**
     * Replace the widgets on specified dashboard with the supplied widgets.
     * 
     * @param widgets - Revised state of widgets to store for the dashboard.
     * @param teamContext - The team context for the operation
     * @param dashboardId - ID of the Dashboard to modify.
     * @param eTag - Dashboard Widgets Version
     */
    public async replaceWidgets(
        widgets: Dashboard.Widget[],
        teamContext: TeamContext.TeamContext,
        dashboardId: string,
        eTag?: String
        ): Promise<Dashboard.WidgetsVersionedList> {

        const project = teamContext.projectId || teamContext.project;
        const team = teamContext.teamId || teamContext.team;

        return this.beginRequest<Response>({
            apiVersion: "6.0-preview.2",
            method: "PUT",
            routeTemplate: "{project}/{team}/_apis/Dashboard/dashboards/{dashboardId}/Widgets/{widgetId}",
            routeValues: {
                project: project,
                team: team,
                dashboardId: dashboardId
            },
            customHeaders: {
                "ETag": eTag,
            },
            body: widgets,
            returnRawResponse: true
        }).then(async response => {
            const body = await response.text().then(deserializeVssJsonObject);
            const eTagHeader = response.headers.get("ETag");
            const result: Dashboard.WidgetsVersionedList = {
                widgets: <Dashboard.Widget[]>body,
                eTag: eTagHeader ? eTagHeader.split(',') : []
            };
            return result;
        });
    }

    /**
     * Perform a partial update of the specified widget.
     * 
     * @param widget - Description of the widget changes to apply. All non-null fields will be replaced.
     * @param teamContext - The team context for the operation
     * @param dashboardId - ID of the dashboard containing the widget.
     * @param widgetId - ID of the widget to update.
     */
    public async updateWidget(
        widget: Dashboard.Widget,
        teamContext: TeamContext.TeamContext,
        dashboardId: string,
        widgetId: string
        ): Promise<Dashboard.Widget> {

        const project = teamContext.projectId || teamContext.project;
        const team = teamContext.teamId || teamContext.team;

        return this.beginRequest<Dashboard.Widget>({
            apiVersion: "6.0-preview.2",
            method: "PATCH",
            routeTemplate: "{project}/{team}/_apis/Dashboard/dashboards/{dashboardId}/Widgets/{widgetId}",
            routeValues: {
                project: project,
                team: team,
                dashboardId: dashboardId,
                widgetId: widgetId
            },
            body: widget
        });
    }

    /**
     * Update the supplied widgets on the dashboard using supplied state. State of existing Widgets not passed in the widget list is preserved.
     * 
     * @param widgets - The set of widget states to update on the dashboard.
     * @param teamContext - The team context for the operation
     * @param dashboardId - ID of the Dashboard to modify.
     * @param eTag - Dashboard Widgets Version
     */
    public async updateWidgets(
        widgets: Dashboard.Widget[],
        teamContext: TeamContext.TeamContext,
        dashboardId: string,
        eTag?: String
        ): Promise<Dashboard.WidgetsVersionedList> {

        const project = teamContext.projectId || teamContext.project;
        const team = teamContext.teamId || teamContext.team;

        return this.beginRequest<Response>({
            apiVersion: "6.0-preview.2",
            method: "PATCH",
            routeTemplate: "{project}/{team}/_apis/Dashboard/dashboards/{dashboardId}/Widgets/{widgetId}",
            routeValues: {
                project: project,
                team: team,
                dashboardId: dashboardId
            },
            customHeaders: {
                "ETag": eTag,
            },
            body: widgets,
            returnRawResponse: true
        }).then(async response => {
            const body = await response.text().then(deserializeVssJsonObject);
            const eTagHeader = response.headers.get("ETag");
            const result: Dashboard.WidgetsVersionedList = {
                widgets: <Dashboard.Widget[]>body,
                eTag: eTagHeader ? eTagHeader.split(',') : []
            };
            return result;
        });
    }

    /**
     * Get the widget metadata satisfying the specified contribution ID.
     * 
     * @param contributionId - The ID of Contribution for the Widget
     * @param project - Project ID or project name
     */
    public async getWidgetMetadata(
        contributionId: string,
        project?: string
        ): Promise<Dashboard.WidgetMetadataResponse> {

        return this.beginRequest<Dashboard.WidgetMetadataResponse>({
            apiVersion: "6.0-preview.1",
            routeTemplate: "{project}/_apis/Dashboard/WidgetTypes/{contributionId}",
            routeValues: {
                project: project,
                contributionId: contributionId
            }
        });
    }

    /**
     * Get all available widget metadata in alphabetical order, including widgets marked with isVisibleFromCatalog == false.
     * 
     * @param scope - 
     * @param project - Project ID or project name
     */
    public async getWidgetTypes(
        scope: Dashboard.WidgetScope,
        project?: string
        ): Promise<Dashboard.WidgetTypesResponse> {

        const queryValues: any = {
            '$scope': scope
        };

        return this.beginRequest<Dashboard.WidgetTypesResponse>({
            apiVersion: "6.0-preview.1",
            routeTemplate: "{project}/_apis/Dashboard/WidgetTypes/{contributionId}",
            routeValues: {
                project: project
            },
            queryParams: queryValues
        });
    }

}

export const DashboardClientName = "IDashboardRestClient";

export function getDashboardClient(pageContext: IVssPageContext, options?: IVssRestClientOptions): IDashboardRestClient {
    return pageContext.getRestClient<IDashboardRestClient>(DashboardClientName, options);
}

RestClients.add(DashboardClientName, { 
    factory: DashboardRestClient,
    options: {
        resourceAreaId: "31c84e0a-3ece-48fd-a29d-100849af99ba"
    }
});
